/**
 * ElevationWidget - Route Elevation Profile Display
 * 
 * Shows elevation profile chart with grade warnings for oilfield truckers
 * Critical safety feature for loaded trucks on hilly terrain
 * 
 * Grade Severity:
 * - Normal (<6%): Green - Safe for all loads
 * - Caution (6-8%): Yellow - Heavy loads may slow
 * - Warning (8-12%): Orange - Use lower gears
 * - Danger (>12%): Red - Extreme grade, avoid if loaded
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Terrain as TerrainIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as UphillIcon,
  TrendingDown as DownhillIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { getCyberColors } from '../theme/cyberColors';

interface ElevationPoint {
  lat: number;
  lng: number;
  elevation: number; // feet
  distance: number; // miles from start
}

interface GradeSegment {
  startIndex: number;
  endIndex: number;
  startElevation: number;
  endElevation: number;
  elevationChange: number;
  distance: number;
  grade: number;
  severity: 'normal' | 'caution' | 'warning' | 'danger';
  direction: 'uphill' | 'downhill' | 'flat';
}

interface ElevationProfile {
  points: ElevationPoint[];
  stats: {
    minElevation: number;
    maxElevation: number;
    totalAscent: number;
    totalDescent: number;
    maxGrade: number;
    maxGradeSeverity: 'normal' | 'caution' | 'warning' | 'danger';
  };
  grades: GradeSegment[];
}

interface ElevationWidgetProps {
  path?: { lat: number; lng: number }[];
  darkMode?: boolean;
  onClose?: () => void;
  onGradeWarning?: (hasWarning: boolean, maxGrade: number) => void;
}

// Severity color mapping
const severityColors = {
  normal: '#22c55e',    // green
  caution: '#eab308',   // yellow
  warning: '#f97316',   // orange
  danger: '#ef4444',    // red
};

const severityLabels = {
  normal: 'SAFE',
  caution: 'CAUTION',
  warning: 'WARNING',
  danger: 'DANGER',
};

export function ElevationWidget({
  path,
  darkMode = true,
  onClose,
  onGradeWarning,
}: ElevationWidgetProps) {
  const cyber = getCyberColors(darkMode);
  const [profile, setProfile] = useState<ElevationProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Close handler
  const handleClose = () => {
    if (onClose) onClose();
  };

  // Fetch elevation profile when route changes
  const fetchElevation = useCallback(async () => {
    if (!path || path.length < 2) {
      setProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format path for API
      const pathString = path
        .map(p => `${p.lat},${p.lng}`)
        .join('|');

      // Limit path length for API
      const truncatedPath = pathString.length > 8000 
        ? path.filter((_, i) => i % 2 === 0).map(p => `${p.lat},${p.lng}`).join('|')
        : pathString;

      const samples = Math.min(path.length * 2, 256);
      const response = await fetch(
        `/api/google/elevation?mode=path&path=${encodeURIComponent(truncatedPath)}&samples=${samples}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch elevation');
      }

      const data = await response.json();
      
      if (data.profile) {
        setProfile(data.profile);
        
        // Notify parent of grade warnings
        if (onGradeWarning) {
          const hasWarning = data.profile.stats.maxGradeSeverity !== 'normal';
          onGradeWarning(hasWarning, data.profile.stats.maxGrade);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Elevation unavailable');
      console.error('Elevation fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [path, onGradeWarning]);

  useEffect(() => {
    if (path && path.length >= 2) {
      fetchElevation();
    }
  }, [fetchElevation, path]);

  // Draw elevation chart on canvas
  useEffect(() => {
    if (!profile || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { points, stats, grades } = profile;
    const width = canvas.width;
    const height = canvas.height;
    const padding = { top: 10, right: 10, bottom: 20, left: 40 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = `${cyber.bgCard}cc`;
    ctx.fillRect(0, 0, width, height);

    if (points.length < 2) return;

    // Calculate scales
    const minElev = stats.minElevation - 50;
    const maxElev = stats.maxElevation + 50;
    const maxDist = points[points.length - 1].distance;

    const xScale = (d: number) => padding.left + (d / maxDist) * chartWidth;
    const yScale = (e: number) => padding.top + chartHeight - ((e - minElev) / (maxElev - minElev)) * chartHeight;

    // Draw grid lines
    ctx.strokeStyle = `${cyber.borderDefault}40`;
    ctx.lineWidth = 0.5;
    
    // Horizontal grid lines
    const elevStep = Math.ceil((maxElev - minElev) / 4 / 100) * 100;
    for (let e = Math.ceil(minElev / elevStep) * elevStep; e <= maxElev; e += elevStep) {
      const y = yScale(e);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
      
      // Elevation label
      ctx.fillStyle = cyber.textSecondary;
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${e}'`, padding.left - 4, y + 3);
    }

    // Draw colored grade segments under the line
    grades.forEach(segment => {
      if (segment.severity === 'normal') return;
      
      const startX = xScale(points[segment.startIndex].distance);
      const endX = xScale(points[segment.endIndex].distance);
      
      ctx.fillStyle = `${severityColors[segment.severity]}30`;
      ctx.fillRect(startX, padding.top, endX - startX, chartHeight);
    });

    // Draw elevation line with gradient
    const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
    gradient.addColorStop(0, cyber.neonCyan);
    gradient.addColorStop(1, `${cyber.neonCyan}40`);

    // Fill area under line
    ctx.beginPath();
    ctx.moveTo(xScale(points[0].distance), height - padding.bottom);
    points.forEach(point => {
      ctx.lineTo(xScale(point.distance), yScale(point.elevation));
    });
    ctx.lineTo(xScale(points[points.length - 1].distance), height - padding.bottom);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw elevation line
    ctx.beginPath();
    ctx.moveTo(xScale(points[0].distance), yScale(points[0].elevation));
    points.forEach(point => {
      ctx.lineTo(xScale(point.distance), yScale(point.elevation));
    });
    ctx.strokeStyle = cyber.neonCyan;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Distance labels
    ctx.fillStyle = cyber.textSecondary;
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('0 mi', padding.left, height - 4);
    ctx.fillText(`${maxDist.toFixed(1)} mi`, width - padding.right, height - 4);

  }, [profile]);

  if (!path || path.length < 2) return null;

  const hasGradeWarning = profile?.stats.maxGradeSeverity && 
    profile.stats.maxGradeSeverity !== 'normal';

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: 120,
        left: 16,
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
        backgroundColor: `${cyber.bgCard}f0`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${hasGradeWarning ? severityColors[profile!.stats.maxGradeSeverity] : cyber.borderDefault}`,
        borderRadius: 2,
        overflow: 'hidden',
        zIndex: 1000,
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          cursor: 'pointer',
          backgroundColor: hasGradeWarning 
            ? `${severityColors[profile!.stats.maxGradeSeverity]}20` 
            : 'transparent',
          '&:hover': { backgroundColor: `${cyber.bgCardHover}80` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TerrainIcon sx={{ color: cyber.neonCyan, fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ color: cyber.textPrimary, fontWeight: 600 }}>
            Elevation Profile
          </Typography>
          {loading && <CircularProgress size={14} sx={{ color: cyber.neonCyan }} />}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {profile && hasGradeWarning && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={`${profile.stats.maxGrade}% MAX`}
              size="small"
              sx={{
                backgroundColor: `${severityColors[profile.stats.maxGradeSeverity]}30`,
                color: severityColors[profile.stats.maxGradeSeverity],
                fontWeight: 700,
                fontSize: 10,
                height: 22,
                '& .MuiChip-icon': {
                  color: severityColors[profile.stats.maxGradeSeverity],
                },
              }}
            />
          )}
          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
            {expanded ? (
              <ExpandLessIcon sx={{ color: cyber.textSecondary, fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ color: cyber.textSecondary, fontSize: 18 }} />
            )}
          </IconButton>
          {onClose && (
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{ ml: -0.5 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cyber.textSecondary} strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        {error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: cyber.textSecondary }}>
              {error}
            </Typography>
          </Box>
        ) : profile ? (
          <Box sx={{ p: 1 }}>
            {/* Elevation Chart Canvas */}
            <canvas
              ref={canvasRef}
              width={300}
              height={100}
              style={{ 
                width: '100%', 
                height: 100, 
                borderRadius: 4,
                border: `1px solid ${cyber.borderDefault}40`,
              }}
            />

            {/* Stats Row */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 1,
              px: 0.5,
            }}>
              <Tooltip title="Minimum elevation">
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', fontSize: 9 }}>
                    MIN
                  </Typography>
                  <Typography variant="body2" sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: 11 }}>
                    {profile.stats.minElevation.toLocaleString()}'
                  </Typography>
                </Box>
              </Tooltip>
              
              <Tooltip title="Maximum elevation">
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', fontSize: 9 }}>
                    MAX
                  </Typography>
                  <Typography variant="body2" sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: 11 }}>
                    {profile.stats.maxElevation.toLocaleString()}'
                  </Typography>
                </Box>
              </Tooltip>
              
              <Tooltip title="Total climb">
                <Box sx={{ textAlign: 'center' }}>
                  <UphillIcon sx={{ color: '#22c55e', fontSize: 12 }} />
                  <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600, fontSize: 11 }}>
                    +{profile.stats.totalAscent.toLocaleString()}'
                  </Typography>
                </Box>
              </Tooltip>
              
              <Tooltip title="Total descent">
                <Box sx={{ textAlign: 'center' }}>
                  <DownhillIcon sx={{ color: '#ef4444', fontSize: 12 }} />
                  <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600, fontSize: 11 }}>
                    -{profile.stats.totalDescent.toLocaleString()}'
                  </Typography>
                </Box>
              </Tooltip>
            </Box>

            {/* Grade Warnings */}
            {profile.grades.filter(g => g.severity !== 'normal').length > 0 && (
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${cyber.borderDefault}40` }}>
                <Typography variant="caption" sx={{ 
                  color: cyber.textSecondary, 
                  display: 'block',
                  mb: 0.5,
                  fontWeight: 600,
                }}>
                  GRADE WARNINGS
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {profile.grades
                    .filter(g => g.severity !== 'normal')
                    .slice(0, 6) // Limit shown warnings
                    .map((segment, i) => (
                      <Tooltip 
                        key={i}
                        title={`${segment.direction === 'uphill' ? '↑' : '↓'} ${Math.abs(segment.grade)}% grade for ${segment.distance} mi`}
                      >
                        <Chip
                          size="small"
                          icon={segment.direction === 'uphill' ? <UphillIcon /> : <DownhillIcon />}
                          label={`${Math.abs(segment.grade)}%`}
                          sx={{
                            backgroundColor: `${severityColors[segment.severity]}20`,
                            color: severityColors[segment.severity],
                            fontWeight: 700,
                            fontSize: 10,
                            height: 20,
                            '& .MuiChip-icon': {
                              color: severityColors[segment.severity],
                              fontSize: 12,
                            },
                          }}
                        />
                      </Tooltip>
                    ))}
                </Box>
              </Box>
            )}

            {/* Legend */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 1, 
              mt: 1,
              pt: 1,
              borderTop: `1px solid ${cyber.borderDefault}40`,
            }}>
              {Object.entries(severityLabels).map(([key, label]) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: severityColors[key as keyof typeof severityColors],
                  }} />
                  <Typography variant="caption" sx={{ color: cyber.textSecondary, fontSize: 8 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={24} sx={{ color: cyber.neonCyan }} />
            <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', mt: 1 }}>
              Loading elevation...
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

export default ElevationWidget;
